import type { ToolActivity } from "../tool-activity";
import type { ToolActivityState } from "../tool-activity-state";
import { describeCloneRepo } from "./describe-clone-repo";
import { GIT_TOOL_NAMES } from "./git-tool-names-constants";
import { GIT_VERBS } from "./git-verbs-constants";
import { gitTarget } from "./git-target";
import { verbLabel } from "./verb-label";

export const gitToolActivityDescribers: readonly [
  string,
  (record: Record<string, unknown>, state: ToolActivityState) => ToolActivity,
][] = [
  [
    GIT_TOOL_NAMES.LIST_REPOS,
    (_, state) => verbLabel(state, GIT_VERBS.LIST, "the repositories"),
  ],
  [GIT_TOOL_NAMES.CLONE_REPO, describeCloneRepo],
  [
    GIT_TOOL_NAMES.REBIND_REPO,
    (record, state) =>
      verbLabel(
        state,
        GIT_VERBS.SWITCH,
        gitTarget.named(record.repoFullName) ?? "another repository",
      ),
  ],
  [
    GIT_TOOL_NAMES.FETCH_PR_REF,
    (record, state) =>
      verbLabel(
        state,
        GIT_VERBS.FETCH,
        gitTarget.pullRequest(record.pullNumber) ?? "a pull request",
      ),
  ],
  [
    GIT_TOOL_NAMES.CLONE_TEMPLATE,
    (_, state) => verbLabel(state, GIT_VERBS.CLONE, "the project template"),
  ],
  [
    GIT_TOOL_NAMES.CREATE_REPO,
    (record, state) => {
      const name = gitTarget.named(record.name);
      return verbLabel(
        state,
        GIT_VERBS.CREATE,
        name ? `repository ${name}` : "the repository",
      );
    },
  ],
  [
    GIT_TOOL_NAMES.CREATE_BRANCH,
    // It may resume an existing branch or prefix the name, so neither is claimed.
    (_, state) => verbLabel(state, GIT_VERBS.SET_UP, "the working branch"),
  ],
  [
    GIT_TOOL_NAMES.GIT_COMMIT,
    (record, state) =>
      verbLabel(
        state,
        GIT_VERBS.COMMIT,
        gitTarget.quoted(record.message) ?? "changes",
      ),
  ],
  [
    GIT_TOOL_NAMES.GIT_PUSH,
    (record, state) =>
      verbLabel(
        state,
        GIT_VERBS.PUSH,
        gitTarget.named(record.branch) ?? "a branch",
      ),
  ],
  [
    GIT_TOOL_NAMES.OPEN_PULL_REQUEST,
    (record, state) => {
      const title = gitTarget.quoted(record.title);
      return verbLabel(
        state,
        GIT_VERBS.OPEN,
        title ? `PR ${title}` : "a pull request",
      );
    },
  ],
  [
    GIT_TOOL_NAMES.GRAPHITE_SUBMIT,
    (_, state) => verbLabel(state, GIT_VERBS.SUBMIT, "the Graphite stack"),
  ],
  [
    GIT_TOOL_NAMES.GITHUB_RESTACK,
    (record, state) =>
      verbLabel(
        state,
        record.continue === true
          ? GIT_VERBS.CONTINUE_RESTACK
          : GIT_VERBS.RESTACK,
        gitTarget.pullRequest(record.prNumber) ?? "the stack",
      ),
  ],
  [
    GIT_TOOL_NAMES.GRAPHITE_RESTACK,
    (record, state) =>
      verbLabel(
        state,
        record.continue === true
          ? GIT_VERBS.CONTINUE_RESTACK
          : GIT_VERBS.RESTACK,
        "the Graphite stack",
      ),
  ],
  [
    GIT_TOOL_NAMES.SUBMIT_PR_REVIEW,
    (record, state) =>
      verbLabel(
        state,
        GIT_VERBS.REVIEW,
        gitTarget.pullRequest(record.pullNumber) ?? "a pull request",
      ),
  ],
  [
    GIT_TOOL_NAMES.ADD_PR_COMMENT,
    (record, state) =>
      typeof record.inReplyTo === "number"
        ? verbLabel(
            state,
            GIT_VERBS.REPLY,
            `to a review comment ${gitTarget.onPullRequest(record.pullNumber)}`,
          )
        : verbLabel(
            state,
            GIT_VERBS.COMMENT,
            gitTarget.onPullRequest(record.pullNumber),
          ),
  ],
  [
    GIT_TOOL_NAMES.RESOLVE_REVIEW_THREAD,
    (record, state) =>
      verbLabel(
        state,
        GIT_VERBS.RESOLVE,
        `a review thread ${gitTarget.onPullRequest(record.pullNumber)}`,
      ),
  ],
  [
    GIT_TOOL_NAMES.REQUEST_MERGE,
    (record, state) =>
      verbLabel(
        state,
        GIT_VERBS.ASK_MERGE,
        gitTarget.pullRequest(record.prNumber) ?? "a pull request",
      ),
  ],
  [
    GIT_TOOL_NAMES.REQUEST_DEPLOY,
    (_, state) => verbLabel(state, GIT_VERBS.ASK_DEPLOY, "to production"),
  ],
];
